/**
 * Auth API functions
 */

import { apiRequest } from "@/lib/queryClient";
import type { LoginCredentials, SignupData, ChangePasswordData, AuthResponse } from "../types";

/**
 * Login with email and password
 */
export async function login(credentials: LoginCredentials): Promise<AuthResponse> {
  const res = await apiRequest("POST", "/api/auth/login", {
    email: credentials.email,
    password: credentials.password,
  });

  return await res.json();
}

/**
 * Sign up a new business account
 */
export async function signup(data: SignupData): Promise<AuthResponse> {
  const res = await apiRequest("POST", "/api/auth/signup", {
    businessName: data.businessName,
    ownerName: data.ownerName,
    ownerEmail: data.ownerEmail,
    password: data.password,
    selectedPlan: data.selectedPlan,
  });

  return await res.json();
}

/**
 * Change password
 */
export async function changePassword(data: ChangePasswordData): Promise<AuthResponse> {
  const res = await apiRequest("POST", "/api/auth/login/change-password", {
    email: data.email,
    currentPassword: data.currentPassword,
    newPassword: data.newPassword,
  });

  return await res.json();
}

/**
 * Request password reset (forgot password)
 */
export async function forgotPassword(email: string): Promise<{ success: boolean; message: string; emailExists?: boolean }> {
  const res = await apiRequest("POST", "/api/auth/login/forgot-password", {
    email,
  });

  return await res.json();
}

/**
 * Reset password with token (no current password required)
 */
export async function resetPassword(token: string, newPassword: string): Promise<{ success: boolean; message: string }> {
  const res = await apiRequest("POST", "/api/auth/login/reset-password", {
    token,
    newPassword,
  });

  return await res.json();
}

/**
 * Logout current user
 * Calls backend to clear refresh token cookie, then clears in-memory access token
 */
export async function logout(): Promise<void> {
  // Use apiClient (axios) instead of apiRequest to ensure cookies are sent
  const { apiClient } = await import("@/lib/axiosClient");
  await apiClient.post("/api/auth/logout");
}

